import $ from "jquery";
import { advertiserLabel, affiliateLabel } from "../utilities/organization_labels";
import DestinationCampaign from './destination_campaign';

export default class VirtualLineMover {
  constructor(config, sourceVirtualLines) {
    this.moveUrl = config.moveUrl;
    this.customDataEnabled = config.customDataEnabled;
    this.moveCustomData = this.customDataEnabled;
    this.sourceVirtualLines = sourceVirtualLines;
    this.moveType = sourceVirtualLines.length > 1 ? "bulk" : "single";
    this.errors = [];

    this.destinationCampaignConfig = {
      destinationAdvertiserCampaignUrl: config.advertiserCampaignsUrl,
      affiliateUrlBase:                 config.affiliateCampaignsUrl,
      validateMoveDestinationUrl:       config.validateMoveDestinationUrl,
      advertiserCampaignId:             config.advertiserCampaignId,
      affiliateCampaignId:              config.affiliateCampaignId,
      requiresAffiliate:                this.requireAffiliate()
    };

    this.destinationCampaign = new DestinationCampaign(this.destinationCampaignConfig);

    this.validate();
  }

  validate() {
    const campaignIds = this.destinationCampaign.campaignIds();
    this.sourceVirtualLines.forEach((sourceVirtualLine) => {
      sourceVirtualLine.validate(campaignIds);
    });

    this.destinationCampaign.validate();
  }

  valid() {
    return (this.errors.length + this.destinationCampaignErrors().length === 0) && this.validSourceNumbers().length > 0;
  }

  virtualLineErrors() {
    return this.sourceVirtualLines.reduce((errors, virtualLine) => {
      const messages = virtualLine.errorMessages;
      if (messages && messages.length > 0) {
        errors[virtualLine.name] = messages;
      }
      return errors;
    }, {});
  }

  lineItemSourceErrors() {
    const lineItems = this.sourceVirtualLines.map((virtualLine) => virtualLine.lineItemErrors());
    return lineItems.filter(item => item);
  }

  destinationCampaignErrors() {
    return [].concat(
      this.destinationCampaign.errors,
      this.destinationCampaign.selectErrors
    ).filter(error => error);
  }

  errorMessages() {
    if (this.errors.length > 0) {
      return this.errors.join("\n");
    }

    const errorCollector = [].concat(
      this.destinationCampaignErrors(),
      this.singleNumberErrors()
    ).filter(error => error);

    if (errorCollector.length > 0) {
      return errorCollector.join("\n");
    }
  }

  destinationSelectError() {
    const errors = this.singleNumberErrors();

    if (errors) {
      return errors.find((error) => error === "Destination cannot be the same as the Promo Number's current campaign");
    }
  }

  singleNumberErrors() {
    if (this.moveType === "single") {
      const number = this.sourceVirtualLines[0].name;
      const errors = this.virtualLineErrors()[number];

      if (errors) {
        return errors;
      }
    }
  }

  requireAffiliate() {
    const affiliateNumbers = this.affiliateSourceNumbers();
    if (affiliateNumbers.length === this.sourceVirtualLines.length) {
      return true;
    } else if (affiliateNumbers.length === 0) {
      return false;
    } else {
      this.errors.push(`Cannot move ${advertiserLabel()} and ${affiliateLabel()} numbers at the same time`);
    }
  }

  affiliateSourceNumbers() {
    return this.sourceVirtualLines.reduce((affiliateNumbers, virtualLine) => {
      if (virtualLine.originalAffiliateCampaignId || virtualLine.isCallPromotion) {
        affiliateNumbers.push(virtualLine);
      }
      return affiliateNumbers;
    }, []);
  }

  async udpateAdvertiserCampaign(event) {
    this.updateDestination({
      advertiserCampaignId: event.id
    });

    if (!this.destinationCampaign.requiresAffiliate) {
      await this.destinationCampaign.ajaxValidations();
    }
  }

  async updateAffiliateId(event) {
    this.updateDestination({
      affiliateCampaignId: event.id,
      advertiserCampaignId: this.destinationCampaign.advertiserCampaignId
    });

    await this.destinationCampaign.ajaxValidations();
  }

  updateDestination(params) {
    this.serverErrors = [];
    this.destinationCampaign = new DestinationCampaign({ ...this.destinationCampaignConfig, ...params });
    this.validate();
  }

  filterSourceNumbers(filterCondition) {
    const errorNumbers = Object.keys(this.virtualLineErrors());
    return this.sourceVirtualLines.filter((sourceVirtualLine) => {
      return filterCondition(sourceVirtualLine, errorNumbers);
    });
  }

  validSourceNumbers() {
    if (this.errors.length === 0) {
      return this.filterSourceNumbers((sourceVirtualLine, errorNumbers) => {
        if (!errorNumbers.includes(sourceVirtualLine.name)) {
          return true;
        }
      });
    } else {
      return [];
    }
  }

  invalidSourceNumbers() {
    return this.filterSourceNumbers((sourceVirtualLine, errorNumbers) => {
      if (errorNumbers.includes(sourceVirtualLine.name)) {
        return true;
      }
    });
  }

  saveData() {
    return {
      ids: this.virtualLineIds(),
      move_custom_data: this.moveCustomData,
      ...this.destinationCampaign.saveData()
    };
  }

  virtualLineIds() {
    return this.validSourceNumbers().map((virtualLine) => virtualLine.id);
  }

  setServerErrors(errorObject) {
    if (typeof errorObject === "string") {
      this.serverErrors = [errorObject];
    } else {
      this.serverErrors = Object.keys(errorObject).map(errorKey => `${errorKey} ${errorObject[errorKey]}`);
    }
  }

  headerErrors() {
    return this.errors.concat(this.serverErrors).filter(error => error);
  }

  async save(successCallback, errorCallback) {
    try {
      const response = await this.post();
      return successCallback(this, response.destination, response.errors);
    } catch (errors) {
      return errorCallback(errors);
    }
  }

  post() {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: this.moveUrl,
        method: "POST",
        data: this.saveData(),
        success: resolve,
        error: ({ responseJSON }) => {
          return reject(this.parseErrors(responseJSON));
        }
      });
    });
  }

  parseErrors(responseJSON) {
    if (responseJSON) {
      return responseJSON.errors;
    } else {
      return "Sorry, an error occurred";
    }
  }
}
